import { useEffect, useState } from "react";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./InstallPrompt.prefix.css";

const DISMISS_KEY = "afri:install-prompt:dismissed-at";
const DISMISS_WINDOW_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

/**
 * PWA install prompt — a dismissible pill at the bottom-right that
 * surfaces the browser's ``beforeinstallprompt`` deferred event.
 *
 * Phase 1 of the offline plan (docs/offline-plan.md). We show this
 * only when:
 *   1. The browser fires ``beforeinstallprompt`` (Chromium + Edge +
 *      Android Chrome; Safari / Firefox never fire it). iOS users
 *      still install via Share → Add to Home Screen; we don't try
 *      to fake a button for them.
 *   2. The app isn't already running in standalone mode (installed).
 *   3. The user hasn't dismissed in the last 14 days. Dismissal is
 *      stored in localStorage so it survives reloads.
 */
export const InstallPrompt = () => {
  const { t } = useTranslation();
  const [deferredEvent, setDeferredEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Already installed? (standalone display mode or iOS home-screen
    // flag). Don't pester.
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (isStandalone) return undefined;

    // Recently dismissed? Respect the 14-day cooldown.
    try {
      const ts = Number.parseInt(localStorage.getItem(DISMISS_KEY) ?? "0", 10);
      if (ts && Date.now() - ts < DISMISS_WINDOW_MS) return undefined;
    } catch (_err) {
      /* localStorage blocked — carry on, worst case we show one extra time */
    }

    const onBeforeInstall = (e) => {
      e.preventDefault(); // keep the browser from auto-popping the mini-infobar
      setDeferredEvent(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferredEvent(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible || !deferredEvent) return null;

  const install = async () => {
    if (busy) return;
    setBusy(true);
    try {
      deferredEvent.prompt();
      const { outcome } = await deferredEvent.userChoice;
      if (outcome === "dismissed") {
        try {
          localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch (_err) {
          /* ignore */
        }
      }
    } finally {
      setBusy(false);
      setVisible(false);
      setDeferredEvent(null);
    }
  };

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_err) {
      /* ignore */
    }
    setVisible(false);
    setDeferredEvent(null);
  };

  const block = cn("install-prompt");
  return (
    <div
      className={block.toClassName()}
      role="dialog"
      aria-label={t("components.installprompt.install_prompt.install_afriannotate")}
    >
      <div className={block.elem("text").toClassName()}>
        <span className={block.elem("title").toClassName()}>
          {t("components.installprompt.install_prompt.install_afriannotate")}
        </span>
        <span className={block.elem("subtitle").toClassName()}>Works offline on your phone and desktop.</span>
      </div>
      <div className={block.elem("actions").toClassName()}>
        <button
          type="button"
          className={block.elem("btn").mod({ ghost: true }).toClassName()}
          onClick={dismiss}
          aria-label={t("components.installprompt.install_prompt.not_now")}
        >
          {t("components.installprompt.install_prompt.not_now")}
        </button>
        <button
          type="button"
          className={block.elem("btn").mod({ primary: true }).toClassName()}
          onClick={install}
          disabled={busy}
          aria-label="Install AfriAnnotate"
        >
          {busy ? "Opening…" : "Install"}
        </button>
      </div>
    </div>
  );
};
